import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorState } from '@/shared/components/error-state';
import { CHART_GRID, chartSeries } from '@/shared/lib/chart-colors';
import { formatCurrency, formatNumber } from '@/shared/lib/format';
import { cn } from '@/shared/lib/utils';

import { useFinancialIssues } from '../api/reports.queries';
import { formatPeriodKey } from '../lib/report-labels';
import type {
  FinancialIssuePoint,
  ReportParams,
  SettlementAgingRow,
  SettlementOutstandingRow,
} from '../types/report.types';
import { AXIS_PROPS } from '../lib/chart-axis';
import { ChartCard, ChartTooltipBox } from './chart-frame';

const PRIMARY = chartSeries(0);
const SECONDARY = chartSeries(1);
const AGING_ORDER = ['0-7', '8-30', '31-60', '60+'];

/** Where each tile leads; a tile without a link (no permission) is plain. */
export interface FinancialIssueLinks {
  pricing?: string;
  refunds?: string;
  reconciliation?: string;
  settlements?: string;
}

/**
 * Financial issues for one currency: orders that lost money or missed the
 * target margin, refunds, unpriced orders, payments that failed with no
 * resolution, and what is outstanding in settlement statements — as tiles that
 * lead to the screen where each is worked, and as trends.
 */
export function FinancialIssues({
  params,
  currency,
  links,
}: {
  params: ReportParams;
  currency: string;
  links: FinancialIssueLinks;
}) {
  const issues = useFinancialIssues(params);

  if (issues.isLoading) return <Skeleton className="h-64" />;
  if (issues.error)
    return <ErrorState error={issues.error} onRetry={() => void issues.refetch()} />;
  if (!issues.data) return null;

  const total = issues.data.totals.find((point) => point.currencyCode === currency);
  const series = issues.data.series.filter((point) => point.currencyCode === currency);
  const outstanding = issues.data.outstanding.filter((row) => row.currencyCode === currency);
  const aging = issues.data.aging
    .filter((row) => row.currencyCode === currency)
    .sort((a, b) => AGING_ORDER.indexOf(a.bucket) - AGING_ORDER.indexOf(b.bucket));
  const outstandingTotal = issues.data.outstandingTotals.find(
    (row) => row.currencyCode === currency,
  );
  const outstandingStatements = outstandingTotal?.statements ?? 0;

  return (
    <div className="space-y-4">
      <ul
        className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
        aria-label={`Financial issues (${currency})`}
      >
        <IssueTile
          title="Loss orders"
          value={formatNumber(total?.lossOrders ?? 0)}
          detail={`${formatCurrency(total?.lossAmount ?? 0, currency)} lost`}
          alert={(total?.lossOrders ?? 0) > 0}
          to={links.pricing}
        />
        <IssueTile
          title="Below target margin"
          value={formatNumber(total?.belowTargetOrders ?? 0)}
          detail={`${formatNumber(total?.atFloorOrders ?? 0)} at or near the floor`}
          alert={(total?.belowTargetOrders ?? 0) > 0}
          to={links.pricing}
        />
        <IssueTile
          title="Refunds"
          value={formatCurrency(total?.refundsAmount ?? 0, currency)}
          detail={`${formatNumber(total?.refundsCount ?? 0)} refunds`}
          alert={(total?.refundsCount ?? 0) > 0}
          to={links.refunds}
        />
        <IssueTile
          title="Unpriced orders"
          value={formatNumber(total?.unpricedOrders ?? 0)}
          detail="No pricing record: no fee, cost or profit known"
          alert={(total?.unpricedOrders ?? 0) > 0}
          to={links.pricing}
        />
        <IssueTile
          title="Failed after payment"
          value={formatNumber(total?.failedAfterPaymentOpen ?? 0)}
          detail="Open, with no reconciliation resolution"
          alert={(total?.failedAfterPaymentOpen ?? 0) > 0}
          to={links.reconciliation}
        />
        <IssueTile
          title="Outstanding settlement"
          value={formatCurrency(outstandingTotal?.receivableOutstanding ?? 0, currency)}
          detail={`Owed to us by channels · we owe partners ${formatCurrency(outstandingTotal?.payableOutstanding ?? 0, currency)} · ${formatNumber(outstandingStatements)} statements`}
          alert={outstandingStatements > 0}
          to={links.settlements}
        />
      </ul>

      <div className="grid gap-4 lg:grid-cols-2">
        <MarginIssuesTrend points={series} />
        <RefundsTrend points={series} currency={currency} />
        <OutstandingByCounterparty rows={outstanding} currency={currency} />
        <SettlementAging rows={aging} currency={currency} />
      </div>
    </div>
  );
}

function IssueTile({
  title,
  value,
  detail,
  alert,
  to,
}: {
  title: string;
  value: string;
  detail: string;
  alert: boolean;
  to?: string;
}) {
  const body = (
    <CardContent className="space-y-0.5 px-4">
      <p className="text-muted-foreground flex items-center justify-between text-[11px] font-medium tracking-wide uppercase">
        {title}
        {to && <ChevronRight className="size-3.5 rtl:rotate-180" aria-hidden />}
      </p>
      <p
        className={cn(
          'font-display text-xl font-bold tabular-nums',
          alert && 'text-status-warning',
        )}
      >
        {value}
      </p>
      <p className="text-muted-foreground text-xs">{detail}</p>
    </CardContent>
  );
  return (
    <li>
      {to ? (
        <Link to={to} aria-label={`${title}: ${value}`} className="block rounded-xl">
          <Card className="hover:border-primary/40 gap-0 py-3 transition-colors">{body}</Card>
        </Link>
      ) : (
        <Card className="gap-0 py-3">{body}</Card>
      )}
    </li>
  );
}

function MarginIssuesTrend({ points }: { points: readonly FinancialIssuePoint[] }) {
  return (
    <ChartCard
      title="Below-target and loss orders"
      legend={[
        { label: 'Below target', color: PRIMARY },
        { label: 'Loss', color: SECONDARY },
      ]}
      isEmpty={points.length === 0}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={[...points]} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={CHART_GRID} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="key"
            tickFormatter={(key: string) => formatPeriodKey(key)}
            minTickGap={24}
            {...AXIS_PROPS}
          />
          <YAxis
            width={40}
            allowDecimals={false}
            tickFormatter={(value: number) => formatNumber(value)}
            {...AXIS_PROPS}
          />
          <Tooltip
            cursor={{ stroke: CHART_GRID }}
            content={({ active, payload, label }) => {
              const point = payload?.[0]?.payload as FinancialIssuePoint | undefined;
              if (!active || !point) return null;
              return (
                <ChartTooltipBox
                  title={formatPeriodKey(String(label), true)}
                  lines={[
                    {
                      label: 'below target',
                      value: formatNumber(point.belowTargetOrders),
                      color: PRIMARY,
                    },
                    { label: 'loss', value: formatNumber(point.lossOrders), color: SECONDARY },
                  ]}
                />
              );
            }}
          />
          <Line
            type="monotone"
            dataKey="belowTargetOrders"
            stroke={PRIMARY}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="lossOrders"
            stroke={SECONDARY}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function RefundsTrend({
  points,
  currency,
}: {
  points: readonly FinancialIssuePoint[];
  currency: string;
}) {
  return (
    <ChartCard
      title={`Refunds (${currency})`}
      isEmpty={points.length === 0}
      emptyTitle="No refunds in this period"
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={[...points]} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id="fin-refunds" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={SECONDARY} stopOpacity={0.24} />
              <stop offset="100%" stopColor={SECONDARY} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={CHART_GRID} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="key"
            tickFormatter={(key: string) => formatPeriodKey(key)}
            minTickGap={24}
            {...AXIS_PROPS}
          />
          <YAxis
            width={56}
            tickFormatter={(value: number) => formatNumber(value)}
            {...AXIS_PROPS}
          />
          <Tooltip
            cursor={{ stroke: CHART_GRID }}
            content={({ active, payload, label }) => {
              const point = payload?.[0]?.payload as FinancialIssuePoint | undefined;
              if (!active || !point) return null;
              return (
                <ChartTooltipBox
                  title={formatPeriodKey(String(label), true)}
                  lines={[
                    {
                      label: `${formatNumber(point.refundsCount)} refunds`,
                      value: formatCurrency(point.refundsAmount, currency),
                      color: SECONDARY,
                    },
                  ]}
                />
              );
            }}
          />
          <Area
            type="monotone"
            dataKey="refundsAmount"
            stroke={SECONDARY}
            strokeWidth={2}
            fill="url(#fin-refunds)"
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

type CounterpartyBar = SettlementOutstandingRow & { name: string };

function OutstandingByCounterparty({
  rows,
  currency,
}: {
  rows: readonly SettlementOutstandingRow[];
  currency: string;
}) {
  const data: CounterpartyBar[] = rows.map((row) => ({
    ...row,
    name: `${row.counterparty} · ${row.countryName ?? `#${row.countryId}`}`,
  }));
  return (
    <ChartCard
      title={`Outstanding by counterparty (${currency})`}
      description="Issued statements not yet settled, whatever their period."
      isEmpty={data.length === 0}
      emptyTitle="Nothing outstanding"
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 0, right: 12, bottom: 0, left: 0 }}
          barCategoryGap={6}
        >
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="name" width={150} {...AXIS_PROPS} />
          <Tooltip
            cursor={{ fill: 'var(--muted)', opacity: 0.5 }}
            content={({ active, payload }) => {
              const row = payload?.[0]?.payload as CounterpartyBar | undefined;
              if (!active || !row) return null;
              return (
                <ChartTooltipBox
                  title={`${row.name} (${row.kind === 'Channel' ? 'owes us' : 'we owe'})`}
                  lines={[
                    { label: 'outstanding', value: formatCurrency(row.outstanding, currency) },
                    { label: 'due', value: formatCurrency(row.due, currency) },
                    { label: 'statements', value: formatNumber(row.statements) },
                  ]}
                />
              );
            }}
          />
          <Bar
            dataKey="outstanding"
            fill={PRIMARY}
            radius={[0, 4, 4, 0]}
            maxBarSize={18}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function SettlementAging({
  rows,
  currency,
}: {
  rows: readonly SettlementAgingRow[];
  currency: string;
}) {
  return (
    <ChartCard
      title={`Settlement aging (${currency})`}
      description="Outstanding amount by days since the statement was issued."
      isEmpty={rows.length === 0}
      emptyTitle="Nothing outstanding"
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={[...rows]} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={CHART_GRID} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="bucket"
            tickFormatter={(bucket: string) => `${bucket} d`}
            {...AXIS_PROPS}
          />
          <YAxis
            width={56}
            tickFormatter={(value: number) => formatNumber(value)}
            {...AXIS_PROPS}
          />
          <Tooltip
            cursor={{ fill: 'var(--muted)', opacity: 0.5 }}
            content={({ active, payload }) => {
              const row = payload?.[0]?.payload as SettlementAgingRow | undefined;
              if (!active || !row) return null;
              return (
                <ChartTooltipBox
                  title={`${row.bucket} days`}
                  lines={[
                    { label: 'outstanding', value: formatCurrency(row.outstanding, currency) },
                    { label: 'statements', value: formatNumber(row.statements) },
                  ]}
                />
              );
            }}
          />
          <Bar
            dataKey="outstanding"
            fill={PRIMARY}
            radius={[4, 4, 0, 0]}
            maxBarSize={36}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
